import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';

// Translations for multi-language support
const TRANSLATIONS = {
  en: {
    t_how: "How this works:",
    t_how_txt: "each section maps to expectations in the contract. Final category is based on total score AND minimum section scores.",
    t_lang: "Language",
    t_emp: "Employee details",
    t_emp_note: "Fill your details, answer all questions, then press Calculate and Send result.",
    t_name: "Full name",
    t_phone: "Phone (optional)",
    t_site: "Project / site (optional)",
    t_to: "Send results to (manager email)",
    t_comments: "Comments (optional)",
    t_honesty: "I confirm I answered honestly. Wrong answers may lead to wrong job level and corrective action.",
    t_quiz: "Quiz sections",
    t_res: "Result",
    t_res_hint: "Complete the quiz and press Calculate result.",
    t_tip: "Tip: if email does not open, copy the result and send it manually.",
    btn_calc: "Calculate result",
    btn_send: "Send result (email)",
    btn_copy: "Copy result",
    btn_reset: "Reset",
  },
  ro: {
    t_how: "Cum functioneaza:",
    t_how_txt: "fiecare sectiune corespunde asteptarilor din contract. Categoria finala se bazeaza pe scorul total SI scorurile minime pe sectiuni.",
    t_lang: "Limba",
    t_emp: "Detalii angajat",
    t_emp_note: "Completeaza datele, raspunde la toate intrebarile, apoi apasa Calculeaza si Trimite rezultatul.",
    t_name: "Nume complet",
    t_phone: "Telefon (optional)",
    t_site: "Proiect / santier (optional)",
    t_to: "Trimite rezultatele la (email manager)",
    t_comments: "Comentarii (optional)",
    t_honesty: "Confirm ca am raspuns sincer. Raspunsurile gresite pot duce la un nivel gresit de job si actiuni corective.",
    t_quiz: "Sectiuni quiz",
    t_res: "Rezultat",
    t_res_hint: "Completeaza quiz-ul si apasa Calculeaza rezultatul.",
    t_tip: "Sfat: daca email-ul nu se deschide, copiaza rezultatul si trimite-l manual.",
    btn_calc: "Calculeaza rezultatul",
    btn_send: "Trimite rezultat (email)",
    btn_copy: "Copiaza rezultat",
    btn_reset: "Reseteaza",
  },
  no: {
    t_how: "Slik fungerer det:",
    t_how_txt: "hver seksjon tilsvarer forventninger i kontrakten. Endelig kategori er basert på total poengsum OG minimumspoeng per seksjon.",
    t_lang: "Språk",
    t_emp: "Ansattdetaljer",
    t_emp_note: "Fyll inn dine detaljer, svar på alle spørsmål, og trykk Beregn og Send resultat.",
    t_name: "Fullt navn",
    t_phone: "Telefon (valgfritt)",
    t_site: "Prosjekt / arbeidssted (valgfritt)",
    t_to: "Send resultater til (leder-e-post)",
    t_comments: "Kommentarer (valgfritt)",
    t_honesty: "Jeg bekrefter at jeg svarte ærlig. Feil svar kan føre til feil jobbnivå og korrigerende tiltak.",
    t_quiz: "Quiz-seksjoner",
    t_res: "Resultat",
    t_res_hint: "Fullfør quizen og trykk Beregn resultat.",
    t_tip: "Tips: hvis e-post ikke åpnes, kopier resultatet og send det manuelt.",
    btn_calc: "Beregn resultat",
    btn_send: "Send resultat (e-post)",
    btn_copy: "Kopier resultat",
    btn_reset: "Tilbakestill",
  }
};

// Quiz sections configuration
const SECTIONS = [
  {
    id: 1,
    title: {
      en: "Technical competence (build & dismantle)",
      ro: "Competenta tehnica (construire & demontare)",
      no: "Teknisk kompetanse (montering & demontering)"
    },
    note: {
      en: "Matches: assembly/dismantle, drawings/instructions, complex systems (L2/L3).",
      ro: "Corespunde: montare/demontare, desene/instructiuni, sisteme complexe (L2/L3).",
      no: "Tilsvarer: montasje/demontering, tegninger/instruksjoner, komplekse systemer (L2/L3)."
    },
    max: 38,
    questions: [
      {
        id: "tech_draw",
        title: {
          en: "Can you build independently based on drawings/instructions?",
          ro: "Poti construi independent pe baza desenelor/instructiunilor?",
          no: "Kan du bygge selvstendig basert på tegninger/instruksjoner?"
        },
        options: [
          { value: 12, label: { en: "Yes, fully independently", ro: "Da, complet independent", no: "Ja, helt selvstendig" } },
          { value: 6, label: { en: "Yes, but need help sometimes", ro: "Da, dar uneori am nevoie de ajutor", no: "Ja, men trenger hjelp noen ganger" } },
          { value: 0, label: { en: "No (need constant supervision)", ro: "Nu (am nevoie de supraveghere constanta)", no: "Nei (trenger konstant tilsyn)" } }
        ]
      },
      {
        id: "tech_complex",
        title: {
          en: "Complex builds (stairs/roofs/cantilever/heavy duty):",
          ro: "Constructii complexe (scari/acoperisuri/console/grele):",
          no: "Komplekse byggerier (trapper/tak/utkragning/tungarbeid):"
        },
        options: [
          { value: 14, label: { en: "Very good (plan & execute)", ro: "Foarte bine (planific si execut)", no: "Veldig bra (planlegger og utfører)" } },
          { value: 10, label: { en: "Good (most cases)", ro: "Bine (in majoritatea cazurilor)", no: "Bra (de fleste tilfeller)" } },
          { value: 5, label: { en: "Basic (simple tasks only)", ro: "De baza (doar sarcini simple)", no: "Grunnleggende (bare enkle oppgaver)" } },
          { value: 0, label: { en: "Not comfortable", ro: "Nu ma simt confortabil", no: "Ikke komfortabel" } }
        ]
      },
      {
        id: "tech_problem",
        title: {
          en: "When something does not fit on site (obstacles/changes), you:",
          ro: "Cand ceva nu se potriveste pe santier (obstacole/modificari), tu:",
          no: "Når noe ikke passer på stedet (hindringer/endringer), du:"
        },
        options: [
          { value: 12, label: { en: "Solve within plan + report deviations immediately", ro: "Rezolv in cadrul planului + raportez deviatiile imediat", no: "Løser innen plan + rapporterer avvik umiddelbart" } },
          { value: 6, label: { en: "Suggest options to TL/Foreman", ro: "Sugerez optiuni catre TL/Sef", no: "Foreslår alternativer til TL/Formann" } },
          { value: 0, label: { en: "Wait for others to decide", ro: "Astept ca altii sa decida", no: "Venter på at andre bestemmer" } }
        ]
      }
    ]
  },
  {
    id: 2,
    title: {
      en: "HSE / HMS & SJA (safety routines)",
      ro: "HSE / HMS & SJA (proceduri de siguranta)",
      no: "HSE / HMS & SJA (sikkerhetsrutiner)"
    },
    note: {
      en: "Matches: participates in SJA/toolbox (L1), leads SJA (TL), daily safety focus (L3).",
      ro: "Corespunde: participa la SJA/toolbox (L1), conduce SJA (TL), focus zilnic pe siguranta (L3).",
      no: "Tilsvarer: deltar i SJA/verktøykasse (L1), leder SJA (TL), daglig sikkerhetsfokus (L3)."
    },
    max: 34,
    questions: [
      {
        id: "hse_sja",
        title: {
          en: "SJA / toolbox talk:",
          ro: "SJA / sedinta de instruire:",
          no: "SJA / verktøykassemøte:"
        },
        options: [
          { value: 14, label: { en: "I can lead SJA for the team", ro: "Pot conduce SJA pentru echipa", no: "Jeg kan lede SJA for teamet" } },
          { value: 8, label: { en: "I participate and follow", ro: "Particip si urmez", no: "Jeg deltar og følger" } },
          { value: 0, label: { en: "I need help / don't know routine", ro: "Am nevoie de ajutor / nu cunosc procedura", no: "Trenger hjelp / kjenner ikke rutinen" } }
        ]
      },
      {
        id: "hse_ppe",
        title: {
          en: "PPE / site rules compliance:",
          ro: "Conformitate EIP / reguli santier:",
          no: "Verneutstyr / etterlevelse av regler:"
        },
        options: [
          { value: 10, label: { en: "I enforce it in the team", ro: "O impun in echipa", no: "Jeg håndhever det i teamet" } },
          { value: 6, label: { en: "Consistent compliance", ro: "Conformitate constanta", no: "Konsekvent etterlevelse" } },
          { value: 0, label: { en: "Sometimes forget / need reminders", ro: "Uneori uit / am nevoie de reamintiri", no: "Glemmer noen ganger / trenger påminnelser" } }
        ]
      },
      {
        id: "hse_stop",
        title: {
          en: "If you see unsafe work:",
          ro: "Daca vezi o munca nesigura:",
          no: "Hvis du ser usikkert arbeid:"
        },
        options: [
          { value: 10, label: { en: "I stop work and correct immediately", ro: "Opresc munca si corectez imediat", no: "Jeg stopper arbeidet og korrigerer umiddelbart" } },
          { value: 6, label: { en: "I inform TL/Foreman", ro: "Informez TL/Seful", no: "Jeg informerer TL/Formann" } },
          { value: 0, label: { en: "I ignore / assume someone else handles it", ro: "Ignor / presupun ca altcineva se ocupa", no: "Jeg ignorerer / antar at noen andre tar seg av det" } }
        ]
      }
    ]
  },
  {
    id: 3,
    title: {
      en: "Quality control & handover readiness",
      ro: "Control calitate & pregatire predare",
      no: "Kvalitetskontroll & overlevering"
    },
    note: {
      en: "Matches: basic quality checks (L2), final checks prior to handover (TL/Foreman).",
      ro: "Corespunde: verificari de baza (L2), verificari finale inainte de predare (TL/Sef).",
      no: "Tilsvarer: grunnleggende kvalitetskontroller (L2), endelige kontroller før overlevering (TL/Formann)."
    },
    max: 22,
    questions: [
      {
        id: "q_checks",
        title: {
          en: "Before inspection/handover you check anchoring, guardrails, access, stability:",
          ro: "Inainte de inspectie/predare verifici ancorarea, balustradele, accesul, stabilitatea:",
          no: "Før inspeksjon/overlevering sjekker du forankring, rekkverk, tilgang, stabilitet:"
        },
        options: [
          { value: 12, label: { en: "Always", ro: "Intotdeauna", no: "Alltid" } },
          { value: 6, label: { en: "Sometimes", ro: "Uneori", no: "Noen ganger" } },
          { value: 0, label: { en: "Rarely", ro: "Rar", no: "Sjelden" } }
        ]
      },
      {
        id: "q_dev",
        title: {
          en: "If you discover a deviation:",
          ro: "Daca descoperi o abatere:",
          no: "Hvis du oppdager et avvik:"
        },
        options: [
          { value: 10, label: { en: "Fix immediately + report", ro: "Repar imediat + raportez", no: "Fikser umiddelbart + rapporterer" } },
          { value: 6, label: { en: "Report it", ro: "O raportez", no: "Rapporterer det" } },
          { value: 0, label: { en: "Leave it for later", ro: "O las pe mai tarziu", no: "Lar det være til senere" } }
        ]
      }
    ]
  },
  {
    id: 4,
    title: {
      en: "Leadership (guiding others)",
      ro: "Leadership (ghidarea altora)",
      no: "Lederskap (veiledning av andre)"
    },
    note: {
      en: "Matches: guides L1 (L2/L3), coordinates daily work (Team Leader).",
      ro: "Corespunde: ghideaza L1 (L2/L3), coordoneaza munca zilnica (Team Leader).",
      no: "Tilsvarer: veileder L1 (L2/L3), koordinerer daglig arbeid (Teamleder)."
    },
    max: 26,
    questions: [
      {
        id: "lead_team",
        title: {
          en: "Have you guided other scaffolders during work?",
          ro: "Ai ghidat alti stilasisti in timpul muncii?",
          no: "Har du veiledet andre stillasarbeidere under arbeid?"
        },
        options: [
          { value: 14, label: { en: "Yes, regularly (small team)", ro: "Da, regulat (echipa mica)", no: "Ja, regelmessig (lite team)" } },
          { value: 8, label: { en: "Yes, 1–2 people sometimes", ro: "Da, 1–2 persoane uneori", no: "Ja, 1–2 personer noen ganger" } },
          { value: 0, label: { en: "Never", ro: "Niciodata", no: "Aldri" } }
        ]
      },
      {
        id: "lead_tasks",
        title: {
          en: "Task distribution:",
          ro: "Distributia sarcinilor:",
          no: "Oppgavefordeling:"
        },
        options: [
          { value: 12, label: { en: "I plan daily tasks and follow up", ro: "Planific sarcinile zilnice si urmaresc", no: "Jeg planlegger daglige oppgaver og følger opp" } },
          { value: 6, label: { en: "I can assign simple tasks", ro: "Pot atribui sarcini simple", no: "Jeg kan tildele enkle oppgaver" } },
          { value: 0, label: { en: "I prefer others to assign tasks", ro: "Prefer ca altii sa atribuie sarcinile", no: "Jeg foretrekker at andre tildeler oppgaver" } }
        ]
      }
    ]
  },
  {
    id: 5,
    title: {
      en: "Client communication & coordination",
      ro: "Comunicare cu clientul & coordonare",
      no: "Kundekommunikasjon & koordinering"
    },
    note: {
      en: "Matches: communicates progress/needs (TL), client communication on site (Foreman).",
      ro: "Corespunde: comunica progresul/nevoile (TL), comunicare cu clientul pe santier (Sef).",
      no: "Tilsvarer: kommuniserer fremgang/behov (TL), kundekommunikasjon på stedet (Formann)."
    },
    max: 22,
    questions: [
      {
        id: "cl_comm",
        title: {
          en: "Client communication on site:",
          ro: "Comunicare cu clientul pe santier:",
          no: "Kundekommunikasjon på stedet:"
        },
        options: [
          { value: 12, label: { en: "Often (planning & coordination)", ro: "Des (planificare & coordonare)", no: "Ofte (planlegging & koordinering)" } },
          { value: 6, label: { en: "Sometimes (simple questions)", ro: "Uneori (intrebari simple)", no: "Noen ganger (enkle spørsmål)" } },
          { value: 0, label: { en: "No", ro: "Nu", no: "Nei" } }
        ]
      },
      {
        id: "cl_rep",
        title: {
          en: "Reporting progress, risks, deviations:",
          ro: "Raportarea progresului, riscurilor, abaterilor:",
          no: "Rapportering av fremgang, risiko, avvik:"
        },
        options: [
          { value: 10, label: { en: "I report early and proactively", ro: "Raportez devreme si proactiv", no: "Jeg rapporterer tidlig og proaktivt" } },
          { value: 5, label: { en: "I report when asked", ro: "Raportez cand sunt intrebat", no: "Jeg rapporterer når jeg blir spurt" } },
          { value: 0, label: { en: "I rarely report", ro: "Raportez rar", no: "Jeg rapporterer sjelden" } }
        ]
      }
    ]
  },
  {
    id: 6,
    title: {
      en: "Materials & equipment responsibility",
      ro: "Responsabilitate materiale & echipamente",
      no: "Ansvar for materialer & utstyr"
    },
    note: {
      en: "Matches: correct handling/return, prevent losses/damage, cleanliness.",
      ro: "Corespunde: manipulare/returnare corecta, prevenirea pierderilor/daunelor, curatenie.",
      no: "Tilsvarer: riktig håndtering/retur, forhindre tap/skade, renslighet."
    },
    max: 20,
    questions: [
      {
        id: "mat_order",
        title: {
          en: "Work area & material handling:",
          ro: "Zona de lucru & manipularea materialelor:",
          no: "Arbeidsområde & materialhåndtering:"
        },
        options: [
          { value: 10, label: { en: "Strict: clean, counted, returned", ro: "Strict: curat, numarat, returnat", no: "Strikt: rent, talt, returnert" } },
          { value: 6, label: { en: "Usually clean and organized", ro: "De obicei curat si organizat", no: "Vanligvis rent og organisert" } },
          { value: 0, label: { en: "Often messy / not my focus", ro: "Adesea dezordonat / nu e focusul meu", no: "Ofte rotete / ikke mitt fokus" } }
        ]
      },
      {
        id: "mat_loss",
        title: {
          en: "If materials/tools are missing or damaged:",
          ro: "Daca materialele/uneltele lipsesc sau sunt deteriorate:",
          no: "Hvis materialer/verktøy mangler eller er skadet:"
        },
        options: [
          { value: 10, label: { en: "Report immediately + prevent repeat", ro: "Raportez imediat + previn repetarea", no: "Rapporterer umiddelbart + forhindrer gjentakelse" } },
          { value: 6, label: { en: "Inform TL/warehouse", ro: "Informez TL/depozitul", no: "Informerer TL/lager" } },
          { value: 0, label: { en: "Ignore / hope it's found later", ro: "Ignor / sper sa se gaseasca mai tarziu", no: "Ignorerer / håper det finnes senere" } }
        ]
      }
    ]
  },
  {
    id: 7,
    title: {
      en: "Productivity & reliability",
      ro: "Productivitate & fiabilitate",
      no: "Produktivitet & pålitelighet"
    },
    note: {
      en: "Matches: expected productivity, deadlines, discipline/pro behavior.",
      ro: "Corespunde: productivitate asteptata, termene, disciplina/comportament profesional.",
      no: "Tilsvarer: forventet produktivitet, frister, disiplin/profesjonell oppførsel."
    },
    max: 22,
    questions: [
      {
        id: "prod_deadline",
        title: {
          en: "Deadlines & pace:",
          ro: "Termene & ritmul:",
          no: "Frister & tempo:"
        },
        options: [
          { value: 12, label: { en: "I plan work to hit deadlines", ro: "Planific munca pentru a respecta termenele", no: "Jeg planlegger arbeidet for å nå fristene" } },
          { value: 6, label: { en: "I usually manage", ro: "De obicei ma descurc", no: "Jeg klarer vanligvis" } },
          { value: 0, label: { en: "I struggle often", ro: "Ma chinui des", no: "Jeg sliter ofte" } }
        ]
      },
      {
        id: "prod_att",
        title: {
          en: "Attendance / punctuality / professionalism:",
          ro: "Prezenta / punctualitate / profesionalism:",
          no: "Oppmøte / punktlighet / profesjonalitet:"
        },
        options: [
          { value: 10, label: { en: "I set the standard for others", ro: "Stabilesc standardul pentru altii", no: "Jeg setter standarden for andre" } },
          { value: 6, label: { en: "Stable and professional", ro: "Stabil si profesional", no: "Stabil og profesjonell" } },
          { value: 0, label: { en: "Needs improvement", ro: "Necesita imbunatatire", no: "Trenger forbedring" } }
        ]
      }
    ]
  }
];

// Level determination logic
const LEVELS = {
  foreman: { min: 180, label: { en: "Foreman", ro: "Sef de santier", no: "Formann" } },
  team_leader: { min: 146, label: { en: "Team Leader", ro: "Team Leader", no: "Teamleder" } },
  l3: { min: 120, label: { en: "Scaffolder Level 3 (Senior)", ro: "Stilasist Nivel 3 (Senior)", no: "Stillasarbeider Nivå 3 (Senior)" } },
  l2: { min: 70, label: { en: "Scaffolder Level 2 (Experienced)", ro: "Stilasist Nivel 2 (Experimentat)", no: "Stillasarbeider Nivå 2 (Erfaren)" } },
  l1: { min: 0, label: { en: "Scaffolder Level 1 (Junior)", ro: "Stilasist Nivel 1 (Junior)", no: "Stillasarbeider Nivå 1 (Junior)" } }
};

const MAX_TOTAL = SECTIONS.reduce((sum, s) => sum + s.max, 0);

const API = process.env.REACT_APP_BACKEND_URL;

function ScaffolderQuiz() {
  const [lang, setLang] = useState('en');
  const [answers, setAnswers] = useState({});
  const [employeeData, setEmployeeData] = useState({
    name: '',
    email: '',
    phone: '',
    site: '',
    comments: '',
    honesty: false
  });
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [existingResult, setExistingResult] = useState(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const t = TRANSLATIONS[lang];

  // Check if email has already submitted
  const checkEmailStatus = async (email) => {
    if (!email || !email.includes('@')) return;
    
    setCheckingEmail(true);
    try {
      const res = await fetch(`${API}/api/quiz/check/${encodeURIComponent(email)}`);
      const data = await res.json();
      
      if (data.has_submitted) {
        setExistingResult(data);
      } else {
        setExistingResult(null);
      }
    } catch (err) {
      console.error('Error checking email:', err);
    } finally {
      setCheckingEmail(false);
    }
  };

  // Debounced email check
  useEffect(() => {
    const timer = setTimeout(() => {
      if (employeeData.email) {
        checkEmailStatus(employeeData.email);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [employeeData.email]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: parseInt(value) }));
  };

  const calculateAndSubmit = async () => {
    // Validate name and email
    if (!employeeData.name.trim()) {
      alert(lang === 'ro' ? 'Te rog sa introduci numele complet!' : 
            lang === 'no' ? 'Vennligst skriv inn fullt navn!' : 
            'Please enter your full name!');
      return;
    }

    if (!employeeData.email || !employeeData.email.includes('@')) {
      alert(lang === 'ro' ? 'Te rog sa introduci o adresa de email valida!' : 
            lang === 'no' ? 'Vennligst skriv inn en gyldig e-postadresse!' : 
            'Please enter a valid email address!');
      return;
    }

    // Check all questions are answered
    const allQuestionIds = SECTIONS.flatMap(s => s.questions.map(q => q.id));
    const allAnswered = allQuestionIds.every(id => answers[id] !== undefined);
    
    if (!allAnswered) {
      alert(lang === 'ro' ? 'Te rog sa raspunzi la toate intrebarile!' : 
            lang === 'no' ? 'Vennligst svar på alle spørsmålene!' : 
            'Please answer all questions!');
      return;
    }

    if (!employeeData.honesty) {
      alert(lang === 'ro' ? 'Te rog sa confirmi onestitatea raspunsurilor!' : 
            lang === 'no' ? 'Vennligst bekreft at du har svart ærlig!' : 
            'Please confirm you answered honestly!');
      return;
    }

    // Calculate section scores
    const sectionScores = SECTIONS.map(section => {
      const score = section.questions.reduce((sum, q) => sum + (answers[q.id] || 0), 0);
      const percentage = Math.round((score / section.max) * 100);
      return {
        id: section.id,
        title: section.title[lang],
        score,
        max: section.max,
        percentage
      };
    });

    const totalScore = sectionScores.reduce((sum, s) => sum + s.score, 0);
    const totalPercentage = Math.round((totalScore / MAX_TOTAL) * 100);

    // Determine level
    let level = LEVELS.l1;
    if (totalScore >= LEVELS.foreman.min) level = LEVELS.foreman;
    else if (totalScore >= LEVELS.team_leader.min) level = LEVELS.team_leader;
    else if (totalScore >= LEVELS.l3.min) level = LEVELS.l3;
    else if (totalScore >= LEVELS.l2.min) level = LEVELS.l2;

    const resultData = {
      sectionScores,
      totalScore,
      totalPercentage,
      maxTotal: MAX_TOTAL,
      level: level.label[lang],
      employeeName: employeeData.name,
      date: new Date().toLocaleDateString()
    };

    // Submit to backend
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/quiz/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employee_name: employeeData.name,
          employee_email: employeeData.email,
          employee_phone: employeeData.phone,
          project_site: employeeData.site,
          comments: employeeData.comments,
          language: lang,
          section_scores: sectionScores,
          total_score: totalScore,
          max_total: MAX_TOTAL,
          total_percentage: totalPercentage,
          recommended_level: level.label[lang],
          answers: answers
        })
      });

      const data = await res.json();

      if (!res.ok) {
        if (data.detail?.code === 'ALREADY_SUBMITTED') {
          alert(lang === 'ro' ? 'Ai completat deja acest quiz! Contacteaza managerul daca trebuie sa il refaci.' : 
                lang === 'no' ? 'Du har allerede fullført denne quizen! Kontakt din leder hvis du trenger å ta den på nytt.' : 
                'You have already completed this quiz! Contact your manager if you need to retake it.');
          setExistingResult({
            has_submitted: true,
            submitted_at: data.detail.submitted_at,
            recommended_level: data.detail.recommended_level
          });
          return;
        }
        throw new Error(data.detail || 'Failed to submit quiz');
      }

      setResult(resultData);
      setSubmitted(true);
      
      // Show success message
      alert(lang === 'ro' ? 'Quiz trimis cu succes! Rezultatele au fost salvate si trimise pe email.' : 
            lang === 'no' ? 'Quiz sendt! Resultatene er lagret og sendt på e-post.' : 
            'Quiz submitted successfully! Results have been saved and emailed.');
            
    } catch (err) {
      console.error('Error submitting quiz:', err);
      alert(lang === 'ro' ? 'Eroare la trimiterea quiz-ului. Te rog incearca din nou.' : 
            lang === 'no' ? 'Feil ved innsending av quiz. Prøv igjen.' : 
            'Error submitting quiz. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getResultText = () => {
    if (!result) return '';
    
    let text = `DOSERNE SCAFFOLDER LEVEL QUIZ RESULT\n`;
    text += `=====================================\n\n`;
    text += `Employee: ${result.employeeName}\n`;
    text += `Date: ${result.date}\n`;
    text += `Email: ${employeeData.email}\n`;
    text += `Phone: ${employeeData.phone || 'N/A'}\n`;
    text += `Project/Site: ${employeeData.site || 'N/A'}\n\n`;
    text += `SECTION SCORES:\n`;
    
    result.sectionScores.forEach(s => {
      text += `  ${s.title}: ${s.score}/${s.max} (${s.percentage}%)\n`;
    });
    
    text += `\nTOTAL: ${result.totalScore}/${result.maxTotal} (${result.totalPercentage}%)\n`;
    text += `RECOMMENDED LEVEL: ${result.level}\n`;
    
    if (employeeData.comments) {
      text += `\nCOMMENTS:\n${employeeData.comments}\n`;
    }
    
    return text;
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(getResultText());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Translations for new messages
  const messages = {
    en: {
      email_label: "Your email (required)",
      email_placeholder: "your.email@example.com",
      already_submitted: "You have already completed this quiz",
      submitted_at: "Submitted at",
      your_level: "Your level",
      contact_manager: "Contact your manager if you need to retake it.",
      submitting: "Submitting...",
      submit_quiz: "Submit Quiz & Send Results"
    },
    ro: {
      email_label: "Email-ul tau (obligatoriu)",
      email_placeholder: "email@exemplu.com",
      already_submitted: "Ai completat deja acest quiz",
      submitted_at: "Trimis la",
      your_level: "Nivelul tau",
      contact_manager: "Contacteaza managerul daca trebuie sa il refaci.",
      submitting: "Se trimite...",
      submit_quiz: "Trimite Quiz & Rezultate"
    },
    no: {
      email_label: "Din e-post (obligatorisk)",
      email_placeholder: "din.epost@eksempel.no",
      already_submitted: "Du har allerede fullført denne quizen",
      submitted_at: "Sendt",
      your_level: "Ditt nivå",
      contact_manager: "Kontakt din leder hvis du trenger å ta den på nytt.",
      submitting: "Sender...",
      submit_quiz: "Send Quiz & Resultater"
    }
  };
  const m = messages[lang];

  return (
    <>
      <Helmet>
        <title>Scaffolder Level Quiz | Doserne</title>
        <meta name="description" content="Internal scaffolder competency quiz for Doserne employees" />
      </Helmet>
      
      <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="scaffolder-quiz-page">
        {/* Header */}
        <header className="border-b border-white/10 bg-[#0a0a0a]/95 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <a href="/" className="text-xl font-bold text-[#23F7DD]">DOSERNE</a>
              <span className="text-white/40">|</span>
              <span className="text-white/70 text-sm">Internal Scaffold Level Quiz</span>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-white/50 text-sm">{t.t_lang}:</label>
              <select 
                value={lang} 
                onChange={(e) => setLang(e.target.value)}
                className="bg-white/5 border border-white/10 rounded px-3 py-1.5 text-sm text-white"
                data-testid="language-selector"
              >
                <option value="en">English</option>
                <option value="ro">Română</option>
                <option value="no">Norsk</option>
              </select>
            </div>
          </div>
        </header>

        <main className="max-w-5xl mx-auto px-4 py-8 space-y-6">
          {/* Info Box */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
            <strong className="text-amber-400">{t.t_how}</strong>{' '}
            <span className="text-white/70">{t.t_how_txt}</span>
          </div>

          {/* Employee Details Card */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <span className="bg-[#23F7DD]/20 text-[#23F7DD] px-3 py-1 rounded-full text-sm font-medium">
                {t.t_emp}
              </span>
            </div>
            <p className="text-white/50 text-sm mb-4">{t.t_emp_note}</p>
            
            {/* Already Submitted Warning */}
            {existingResult && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 text-red-400 font-semibold mb-2">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  {m.already_submitted}
                </div>
                <p className="text-white/70 text-sm">
                  {m.submitted_at}: {new Date(existingResult.submitted_at).toLocaleString()}<br />
                  {m.your_level}: <strong className="text-green-400">{existingResult.recommended_level}</strong><br />
                  {m.contact_manager}
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/70 text-sm mb-1">{t.t_name} *</label>
                <input
                  type="text"
                  value={employeeData.name}
                  onChange={(e) => setEmployeeData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Ion Popescu"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder:text-white/30"
                  data-testid="employee-name-input"
                  disabled={submitted || existingResult}
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">{m.email_label} *</label>
                <input
                  type="email"
                  value={employeeData.email}
                  onChange={(e) => setEmployeeData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder={m.email_placeholder}
                  className={`w-full bg-white/5 border rounded-lg px-4 py-2.5 text-white placeholder:text-white/30 ${
                    existingResult ? 'border-red-500/50' : 'border-white/10'
                  }`}
                  data-testid="employee-email-input"
                  disabled={submitted}
                />
                {checkingEmail && <p className="text-white/40 text-xs mt-1">Checking...</p>}
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">{t.t_phone}</label>
                <input
                  type="text"
                  value={employeeData.phone}
                  onChange={(e) => setEmployeeData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+47 ..."
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder:text-white/30"
                  disabled={submitted || existingResult}
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">{t.t_site}</label>
                <input
                  type="text"
                  value={employeeData.site}
                  onChange={(e) => setEmployeeData(prev => ({ ...prev, site: e.target.value }))}
                  placeholder="e.g., Oslo"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder:text-white/30"
                  disabled={submitted || existingResult}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-white/70 text-sm mb-1">{t.t_comments}</label>
                <textarea
                  value={employeeData.comments}
                  onChange={(e) => setEmployeeData(prev => ({ ...prev, comments: e.target.value }))}
                  placeholder="Anything you want to add..."
                  rows={3}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder:text-white/30 resize-none"
                  disabled={submitted || existingResult}
                />
              </div>
              <div className="md:col-span-2">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={employeeData.honesty}
                    onChange={(e) => setEmployeeData(prev => ({ ...prev, honesty: e.target.checked }))}
                    className="mt-1 w-5 h-5 rounded border-white/20 bg-white/5 text-[#23F7DD] focus:ring-[#23F7DD]"
                    data-testid="honesty-checkbox"
                    disabled={submitted || existingResult}
                  />
                  <span className="text-white/60 text-sm">{t.t_honesty}</span>
                </label>
              </div>
            </div>
          </div>

          {/* Quiz Sections Card */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="bg-[#23F7DD]/20 text-[#23F7DD] px-3 py-1 rounded-full text-sm font-medium">
                {t.t_quiz}
              </span>
            </div>

            <div className="space-y-8">
              {SECTIONS.map((section, sIdx) => (
                <div key={section.id} className="border-b border-white/5 pb-6 last:border-0 last:pb-0">
                  {/* Section Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-[#23F7DD]/10 rounded-lg flex items-center justify-center text-[#23F7DD] font-bold">
                        {String(section.id).padStart(2, '0')}
                      </div>
                      <div>
                        <h3 className="text-white font-semibold">{section.title[lang]}</h3>
                        <p className="text-white/40 text-sm mt-1">{section.note[lang]}</p>
                      </div>
                    </div>
                    <div className="text-white/40 text-sm">Max: <b className="text-white">{section.max}</b></div>
                  </div>

                  {/* Questions */}
                  <div className="space-y-4 ml-14">
                    {section.questions.map((question, qIdx) => (
                      <div key={question.id} className="bg-white/3 rounded-lg p-4">
                        <div className="text-white/90 font-medium mb-3">
                          {String(section.id).padStart(2, '0')}.{String(qIdx + 1).padStart(2, '0')} — {question.title[lang]}
                        </div>
                        <div className="space-y-2">
                          {question.options.map((option, oIdx) => (
                            <label 
                              key={oIdx}
                              className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                                answers[question.id] === option.value 
                                  ? 'bg-[#23F7DD]/10 border border-[#23F7DD]/30' 
                                  : 'bg-white/5 border border-transparent hover:border-white/10'
                              }`}
                            >
                              <input
                                type="radio"
                                name={question.id}
                                value={option.value}
                                checked={answers[question.id] === option.value}
                                onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                                className="w-4 h-4 text-[#23F7DD] bg-white/5 border-white/20 focus:ring-[#23F7DD]"
                              />
                              <span className="text-white/80 font-medium">{option.label[lang]}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t border-white/10">
              {!submitted && !existingResult ? (
                <button
                  onClick={calculateAndSubmit}
                  disabled={submitting}
                  className="px-6 py-2.5 bg-[#23F7DD] text-black font-semibold rounded-lg hover:bg-[#1de0c9] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  data-testid="submit-btn"
                >
                  {submitting ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {m.submitting}
                    </>
                  ) : m.submit_quiz}
                </button>
              ) : (
                <div className="text-green-400 font-medium flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {lang === 'ro' ? 'Quiz trimis cu succes!' : lang === 'no' ? 'Quiz sendt!' : 'Quiz submitted!'}
                </div>
              )}
              {result && (
                <button
                  onClick={handleCopy}
                  className="px-6 py-2.5 bg-white/10 text-white font-medium rounded-lg hover:bg-white/20 transition-colors"
                  data-testid="copy-btn"
                >
                  {copied ? '✓ Copied!' : t.btn_copy}
                </button>
              )}
            </div>
            {!submitted && !existingResult && (
              <p className="text-amber-400/80 text-sm mt-3">
                {lang === 'ro' ? '⚠️ Atentie: Quiz-ul poate fi completat o singura data. Rezultatele vor fi trimise automat pe email.' : 
                 lang === 'no' ? '⚠️ Merk: Quizen kan bare fullføres én gang. Resultatene sendes automatisk på e-post.' : 
                 '⚠️ Note: This quiz can only be completed once. Results will be automatically emailed.'}
              </p>
            )}
          </div>

          {/* Result Card */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="bg-[#23F7DD]/20 text-[#23F7DD] px-3 py-1 rounded-full text-sm font-medium">
                {t.t_res}
              </span>
            </div>
            
            {result ? (
              <div className="space-y-4" data-testid="quiz-result">
                {/* Overall Score */}
                <div className="bg-[#23F7DD]/10 border border-[#23F7DD]/30 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-semibold">Total Score</span>
                    <span className="text-[#23F7DD] font-bold text-xl">
                      {result.totalScore}/{result.maxTotal} ({result.totalPercentage}%)
                    </span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-3">
                    <div 
                      className="bg-[#23F7DD] h-3 rounded-full transition-all duration-500"
                      style={{ width: `${result.totalPercentage}%` }}
                    />
                  </div>
                </div>

                {/* Recommended Level */}
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                  <div className="text-white/60 text-sm mb-1">Recommended Level</div>
                  <div className="text-green-400 font-bold text-2xl" data-testid="recommended-level">
                    {result.level}
                  </div>
                </div>

                {/* Section Breakdown */}
                <div className="space-y-3">
                  <h4 className="text-white/70 font-medium">Section Breakdown:</h4>
                  {result.sectionScores.map(s => (
                    <div key={s.id} className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-white/5 rounded flex items-center justify-center text-white/50 text-sm font-bold">
                        {String(s.id).padStart(2, '0')}
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-white/70">{s.title}</span>
                          <span className="text-white">{s.score}/{s.max}</span>
                        </div>
                        <div className="w-full bg-white/10 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-500 ${
                              s.percentage >= 70 ? 'bg-green-500' : 
                              s.percentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${s.percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-white/40">{t.t_res_hint}</p>
            )}
          </div>
        </main>

        {/* Footer */}
        <footer className="border-t border-white/10 py-6 mt-12">
          <div className="max-w-5xl mx-auto px-4 text-center text-white/40 text-sm">
            © {new Date().getFullYear()} Doserne Stillas AS. Internal use only.
          </div>
        </footer>
      </div>
    </>
  );
}

export default ScaffolderQuiz;
